import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { groupApi } from '../../../../core/api/rtk-query/group.api';
import { userApi } from '../../../../core/api/rtk-query/user.api';
import { isuGroupApi } from '../../../../core/api/rtk-query/isu-group.api';
import { eventApi } from '../../../../core/api/rtk-query/event.api';
import { EventsGrid } from '../../../common/main/events/grid/EventsGrid';
import { EventType } from '../../../../core/types/event.interface';
import { getInfo, getMaxPoints, getUserPoints } from '../../../../utils/cards.utils';
import { EventCardProps } from '../../../common/main/events/grid/card/types';
import { ROUTES } from '../../../../routes/routes.config';
import toast from 'react-hot-toast';

const Wrap = styled.div`
  display: grid;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  padding: 20px 24px;
  border-radius: 12px;
  border: 1px solid #e5e8f0;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: #f7f9ff;
  }

  &::before {
    content: '←';
    font-size: 24px;
    color: #0b1221;
  }
`;

const Grow = styled.div`
  flex: 1;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 4px;
  background: #f7f9ff;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid #dbe3f6;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: ${p => (p.$active ? '#fff' : 'transparent')};
  color: ${p => (p.$active ? '#0b1221' : '#667')};
  border: ${p => (p.$active ? '1px solid #dbe3f6' : '1px solid transparent')};
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: ${p => (p.$active ? 600 : 400)};
  font-size: 14px;
  transition: all 0.2s;
  white-space: nowrap;
  box-shadow: ${p => (p.$active ? '0 2px 8px rgba(0,0,0,0.05)' : 'none')};

  &:hover {
    color: #0b1221;
  }
`;

const Content = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #e5e8f0;
  min-height: 400px;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const CreateButton = styled.button`
  background: #0b68fe;
  color: #fff;
  border: none;
  padding: 10px 16px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;

  &:hover {
    background: #0952cc;
  }

  &::before {
    content: '+';
    font-size: 18px;
  }
`;

const EventTypeTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const EventTypeTab = styled.button<{ $active: boolean }>`
  background: ${p => (p.$active ? '#0b68fe' : '#fff')};
  color: ${p => (p.$active ? '#fff' : '#667')};
  border: 1px solid ${p => (p.$active ? '#0b68fe' : '#dbe3f6')};
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    border-color: #0b68fe;
    color: ${p => (p.$active ? '#fff' : '#0b68fe')};
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #dbe3f6;
  background: #fff;
  color: #0b1221;
  width: 100%;
  box-sizing: border-box;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #0b68fe;
  }
`;

const Btn = styled.button`
  background: #fff;
  border: 1px solid #dbe3f6;
  color: #0b1221;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;

  &:hover {
    background: #f7f9ff;
  }
`;

const DangerBtn = styled(Btn)`
  border-color: #f5c2c7;
  color: #9b1c1c;
  background: #fde8ea;

  &:hover {
    background: #fcd2d7;
  }
`;

const SearchSection = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e8f0;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #0b1221;
`;

const UserList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e8f0;
  border-radius: 12px;
  margin-top: 12px;
`;

const UserRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #eef1f6;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f7f9ff;
  }
`;

const IsuGroupsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

const IsuGroupCard = styled.div`
  background: #f7f9ff;
  border: 1px solid #dbe3f6;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IsuGroupName = styled.div`
  font-weight: 600;
  color: #0b1221;
  font-size: 14px;
`;

const IsuGroupCount = styled.div`
  font-size: 12px;
  color: #667;
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #667;
  font-size: 14px;
`;

export const TeacherGroupDetailPage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'events' | 'students'>('events');
  const [eventTypeFilter, setEventTypeFilter] = useState<'all' | EventType>('all');

  // Загружаем данные группы
  const { data: activeGroups } = groupApi.useGetActiveGroupQuery();
  const { data: archivedGroups } = groupApi.useGetArchivedGroupQuery();
  const allGroups = [...(activeGroups || []), ...(archivedGroups || [])];
  const group = allGroups.find(g => g.id === groupId);

  // Загружаем мероприятия группы
  const { data: allEvents } = eventApi.useGetTeacherEventsQuery();
  const groupEvents = useMemo(
    () => (allEvents || []).filter(e => e.groupId === groupId),
    [allEvents, groupId]
  );

  // Фильтрация мероприятий по типу
  const filteredEvents = useMemo(() => {
    if (eventTypeFilter === 'all') return groupEvents;
    return groupEvents.filter(e => e.type === eventTypeFilter);
  }, [groupEvents, eventTypeFilter]);

  // Преобразование в карточки
  const eventCards: EventCardProps[] = useMemo(() => {
    return filteredEvents.map(event => ({
      id: event.id,
      name: event.name,
      info: getInfo(event),
      description: event.description || '',
      buttons: {
        blueButtonText: 'Открыть',
        whiteButtonText: '',
      },
    }));
  }, [filteredEvents]);

  // Загружаем участников группы
  const { data: membersPage, refetch: refetchMembers } = groupApi.useGetUsersInGroupQuery(
    { groupId: groupId || '', pageNumber: 1, pageSize: 100 },
    { skip: !groupId }
  );

  // Поиск студентов
  const [searchQuery, setSearchQuery] = useState('');
  const [triggerSearch] = userApi.useLazySearchUsersQuery();
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Мутации
  const [addUserToGroup] = groupApi.useAddUserToGroupMutation();
  const [removeUserFromGroup] = groupApi.useRemoveUserFromGroupMutation();

  // Группы ИСУ
  const [isuGroupSearch, setIsuGroupSearch] = useState('');
  const { data: isuGroups } = isuGroupApi.useGetIsuGroupsQuery();
  const [selectedIsuGroup, setSelectedIsuGroup] = useState<string | null>(null);
  const { data: isuGroupUsers } = isuGroupApi.useGetUsersByIsuGroupQuery(
    { name: selectedIsuGroup || '', pageNumber: 1, pageSize: 100 },
    { skip: !selectedIsuGroup }
  );

  // Поиск студентов с дебаунсом
  useEffect(() => {
    const timeout = setTimeout(async () => {
      const q = searchQuery.trim();
      if (!q) {
        setSearchResults([]);
        return;
      }
      const isNum = /^\d+$/.test(q);
      const tasks: Promise<any>[] = [];
      tasks.push(triggerSearch({ name: q, role: 'Student', pageNumber: 1, pageSize: 25 }).unwrap());
      tasks.push(triggerSearch({ email: q, role: 'Student', pageNumber: 1, pageSize: 25 }).unwrap());
      tasks.push(triggerSearch({ isuGroup: q, role: 'Student', pageNumber: 1, pageSize: 25 }).unwrap());
      if (isNum) tasks.push(triggerSearch({ isuNumber: Number(q), role: 'Student', pageNumber: 1, pageSize: 25 }).unwrap());
      const settled = await Promise.allSettled(tasks);
      const merged: Record<string, any> = {};
      settled.forEach(r => {
        if (r.status === 'fulfilled') for (const u of r.value?.users || []) merged[u.id] = u;
      });
      setSearchResults(Object.values(merged));
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, triggerSearch]);

  const students = useMemo(
    () => (membersPage?.users || []).filter((u: any) => u.role === 'Student'),
    [membersPage]
  );

  const filteredIsuGroups = useMemo(
    () =>
      (isuGroups || [])
        .filter(g => g.name.toLowerCase().includes(isuGroupSearch.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 20),
    [isuGroups, isuGroupSearch]
  );

  const handleAddStudent = async (userId: string) => {
    try {
      await addUserToGroup({ userId, groupId: groupId || '' }).unwrap();
      await refetchMembers();
      setSearchQuery('');
      toast.success('Студент добавлен');
    } catch (error) {
      toast.error('Ошибка при добавлении студента');
    }
  };

  const handleRemoveStudent = async (userId: string) => {
    try {
      await removeUserFromGroup({ userId, groupId: groupId || '' }).unwrap();
      await refetchMembers();
      toast.success('Студент удален');
    } catch (error) {
      toast.error('Ошибка при удалении студента');
    }
  };

  const handleAddIsuGroup = async () => {
    if (!selectedIsuGroup || !isuGroupUsers?.users) return;

    try {
      const studentIds = isuGroupUsers.users.filter((u: any) => u.role === 'Student').map((u: any) => u.id);

      for (const userId of studentIds) {
        try {
          await addUserToGroup({ userId, groupId: groupId || '' }).unwrap();
        } catch (err) {
          // Игнорируем ошибки если студент уже добавлен
          console.warn('Failed to add user:', err);
        }
      }

      await refetchMembers();
      setSelectedIsuGroup(null);
      setIsuGroupSearch('');
      toast.success(`Добавлено студентов из группы ${selectedIsuGroup}: ${studentIds.length}`);
    } catch (error) {
      toast.error('Ошибка при добавлении группы ИСУ');
    }
  };

  if (!group) {
    return (
      <Wrap>
        <Header>
          <BackButton onClick={() => navigate(ROUTES.teacher.groups.fullPath)} />
          <h2 style={{ margin: 0 }}>Курс не найден</h2>
        </Header>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Header>
        <BackButton onClick={() => navigate(ROUTES.teacher.groups.fullPath)} />
        <h2 style={{ margin: 0 }}>Курс: {group.name}</h2>
        <Grow />
        <TabsContainer>
          <Tab $active={activeTab === 'events'} onClick={() => setActiveTab('events')}>
            Мероприятия
          </Tab>
          <Tab $active={activeTab === 'students'} onClick={() => setActiveTab('students')}>
            Студенты ({students.length})
          </Tab>
        </TabsContainer>
      </Header>

      {activeTab === 'events' && (
        <Content>
          <ContentHeader>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 600 }}>Мероприятия курса</h3>
              <div style={{ fontSize: 14, color: '#667' }}>Всего: {groupEvents.length}</div>
            </div>
            <CreateButton onClick={() => navigate(ROUTES.teacher.createTask.fullPath)}>
              Новое мероприятие
            </CreateButton>
          </ContentHeader>

          <EventTypeTabs>
            <EventTypeTab $active={eventTypeFilter === 'all'} onClick={() => setEventTypeFilter('all')}>
              Все ({groupEvents.length})
            </EventTypeTab>
            <EventTypeTab
              $active={eventTypeFilter === EventType.Homework}
              onClick={() => setEventTypeFilter(EventType.Homework)}
            >
              Домашние работы ({groupEvents.filter(e => e.type === EventType.Homework).length})
            </EventTypeTab>
            <EventTypeTab
              $active={eventTypeFilter === EventType.Contest}
              onClick={() => setEventTypeFilter(EventType.Contest)}
            >
              Контрольные ({groupEvents.filter(e => e.type === EventType.Contest).length})
            </EventTypeTab>
            <EventTypeTab
              $active={eventTypeFilter === EventType.Milestone}
              onClick={() => setEventTypeFilter(EventType.Milestone)}
            >
              Рубежные ({groupEvents.filter(e => e.type === EventType.Milestone).length})
            </EventTypeTab>
            <EventTypeTab $active={eventTypeFilter === EventType.Exam} onClick={() => setEventTypeFilter(EventType.Exam)}>
              Экзамены ({groupEvents.filter(e => e.type === EventType.Exam).length})
            </EventTypeTab>
          </EventTypeTabs>

          {eventCards.length > 0 ? <EventsGrid events={eventCards} /> : <EmptyState>Нет мероприятий</EmptyState>}
        </Content>
      )}

      {activeTab === 'students' && (
        <Content>
          <SearchSection>
            <SectionTitle>Поиск студентов</SectionTitle>
            <Input
              placeholder="Имя, почта, ИСУ или группа ИСУ..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && searchResults.length > 0 && (
              <UserList>
                {searchResults.map(u => (
                  <UserRow key={u.id}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{u.name}</div>
                      <div style={{ fontSize: 12, color: '#667', marginTop: 4 }}>
                        ИСУ: {u.isuNumber} | {u.email} | Группа: {u.isuGroup || '—'}
                      </div>
                    </div>
                    <Btn onClick={() => handleAddStudent(u.id)}>Добавить</Btn>
                  </UserRow>
                ))}
              </UserList>
            )}
          </SearchSection>

          <SearchSection>
            <SectionTitle>Добавить группу ИСУ целиком</SectionTitle>
            <Input
              placeholder="Поиск группы ИСУ (например, M3101)..."
              value={isuGroupSearch}
              onChange={e => setIsuGroupSearch(e.target.value)}
            />
            {isuGroupSearch && filteredIsuGroups.length > 0 && (
              <IsuGroupsGrid>
                {filteredIsuGroups.map(g => (
                  <IsuGroupCard key={g.name}>
                    <IsuGroupName>{g.name}</IsuGroupName>
                    <IsuGroupCount>{g.count} студентов</IsuGroupCount>
                    <Btn
                      onClick={() => {
                        setSelectedIsuGroup(g.name);
                        setTimeout(() => handleAddIsuGroup(), 100);
                      }}
                    >
                      Добавить всех
                    </Btn>
                  </IsuGroupCard>
                ))}
              </IsuGroupsGrid>
            )}
          </SearchSection>

          <div>
            <SectionTitle>Студенты курса ({students.length})</SectionTitle>
            {students.length > 0 ? (
              <UserList>
                {students.map((u: any) => (
                  <UserRow key={u.id}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{u.name}</div>
                      <div style={{ fontSize: 12, color: '#667', marginTop: 4 }}>
                        ИСУ: {u.isuNumber} | {u.email} | Группа: {u.isuGroup || '—'}
                      </div>
                    </div>
                    <DangerBtn onClick={() => handleRemoveStudent(u.id)}>Удалить</DangerBtn>
                  </UserRow>
                ))}
              </UserList>
            ) : (
              <EmptyState>Нет студентов на курсе</EmptyState>
            )}
          </div>
        </Content>
      )}
    </Wrap>
  );
};
